import { Component, OnDestroy } from '@angular/core';

import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { first, map, Observable, Subject, switchMap, takeUntil } from 'rxjs';

import { ApiSubjectCategory, ApiThema, SubjectCategoryType } from 'content-manager/src/app/core/open-api/content-manager-api-client';
import { IProjectUpdateThemasDialogData } from 'content-manager/src/app/projects/project/themas/update-themas-dialog/project-update-themas-dialog-data';

export interface ThemaGroup {
    parent: ApiThema;
    children: ApiThema[];
}

@Component({
    templateUrl: './project-update-themas-dialog.component.html',
    standalone: false
})
export class ProjectUpdateThemasDialogComponent implements OnDestroy {

    public readonly dialogData: IProjectUpdateThemasDialogData;

    public selectedThemaCodes: string[] = [];

    public requestInProcess: boolean;

    public themaGroups$: Observable<ThemaGroup[]>;

    private readonly _destroy$ = new Subject<void>();

    constructor(
        dialogConfig: DynamicDialogConfig,
        private readonly _dialogRef: DynamicDialogRef) {

        this.dialogData = dialogConfig.data;

        this.themaGroups$ = this.dialogData.themas$.pipe(
            map(themas => this._groupThemas(themas))
        );

        this.dialogData.existingThemaCodes$
            .pipe(takeUntil(this._destroy$))
            .subscribe(themaCodes => this.selectedThemaCodes = themaCodes);
    }

    public save(): void {
        this.requestInProcess = true;

        this.themaGroups$.pipe(
            first(),
            switchMap(themaGroups => {
                const subjectCategories = this._buildSubjectCategories(themaGroups);
                return this.dialogData.updateAction(subjectCategories);
            }),
            takeUntil(this._destroy$)
        ).subscribe(() => {
            this.requestInProcess = false;
            this._dialogRef.close(true);
        });
    }

    private _buildSubjectCategories(themaGroups: ThemaGroup[]): ApiSubjectCategory[] {
        const allThemas = themaGroups.flatMap(g => g.children);
        return this.selectedThemaCodes
            .map(code => allThemas.find(t => t.code === code))
            .filter((t): t is ApiThema => !!t)
            .map(thema => new ApiSubjectCategory({
                subjectCategoryType: SubjectCategoryType.Thema,
                code: thema.code,
                headingText: thema.heading
            }));
    }

    public ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    private _groupThemas(themas: ApiThema[]): ThemaGroup[] {
        const parentThemas = themas.filter(t => !t.parentCode);
        const childThemas = themas.filter(t => t.parentCode);

        return parentThemas.map(parent => ({
            parent,
            children: childThemas.filter(child => child.parentCode === parent.code)
        }));
    }
}
